import { sd } from "@sd/lib/sdPaths";
import {
  normalizeCreatedDateFilter,
  type CreatedDateFilterState,
} from "@sd/lib/ticketCreatedDateFilter";
import {
  emptyStatusDashboardFilters,
  formatStatusDashboardProjectParam,
  parseStatusDashboardProjectParam,
  type StatusDashboardFilters,
} from "@sd/lib/statusDashboardApi";
import { sanitizeStatusFilterValues, type TicketStatus } from "@sd/constants/ticketStatuses";
import {
  persistStatusDashboardReturnFilters,
  readStatusDashboardReturnFilters,
} from "@sd/lib/ticketListDrillDownStorage";

export type StatusDashboardPanel = "admin" | "agent";

export const STATUS_DASHBOARD_FROM_QUERY = "from";
export const STATUS_DASHBOARD_FROM_VALUE = "project-status";

export function statusDashboardPagePath(panel: StatusDashboardPanel): string {
  return panel === "admin"
    ? sd("admin/project-status")
    : sd("tickets/project-status");
}

export function statusDashboardTicketsListPath(
  panel: StatusDashboardPanel
): string {
  return panel === "admin" ? sd("admin/tickets") : sd("tickets/all");
}

function appendStatusDashboardFilterParams(
  params: URLSearchParams,
  filters: StatusDashboardFilters
): void {
  const project = formatStatusDashboardProjectParam(filters.projectFilters);
  if (project) params.set("project", project);

  const dates = normalizeCreatedDateFilter(filters.createdDateFilter);
  if (dates.from) params.set("start_date", dates.from);
  if (dates.to) params.set("end_date", dates.to);
}

/** Ticket list → Dashboard Project Status with same project/date filters. */
export function buildStatusDashboardReturnPath(
  panel: StatusDashboardPanel,
  filters: StatusDashboardFilters
): string {
  const params = new URLSearchParams();
  appendStatusDashboardFilterParams(params, filters);
  const query = params.toString();
  const base = statusDashboardPagePath(panel);
  return query ? `${base}?${query}` : base;
}

/** Read dashboard filter context from ticket-list URL (`from=project-status`). */
export function parseStatusDashboardReturnContext(
  searchParams: URLSearchParams
): StatusDashboardFilters | null {
  if (!isStatusDashboardReturnContext(searchParams)) return null;

  const startDate = searchParams.get("start_date")?.trim();
  const endDate = searchParams.get("end_date")?.trim();
  const projectFilters = parseStatusDashboardProjectParam(
    searchParams.get("project")
  );

  if (projectFilters.length > 0 || startDate || endDate) {
    return {
      projectFilters,
      createdDateFilter: normalizeCreatedDateFilter({
        from: startDate ?? "",
        to: endDate ?? "",
      }),
    };
  }

  return readStatusDashboardReturnFilters() ?? emptyStatusDashboardFilters();
}

/** Restore dashboard filters from page URL query params. */
export function parseStatusDashboardPageSearchParams(
  searchParams: URLSearchParams
): StatusDashboardFilters | null {
  const projectFilters = parseStatusDashboardProjectParam(
    searchParams.get("project")
  );
  const startDate = searchParams.get("start_date")?.trim();
  const endDate = searchParams.get("end_date")?.trim();

  if (
    projectFilters.length === 0 &&
    !startDate &&
    !endDate
  ) {
    return null;
  }

  return {
    projectFilters,
    createdDateFilter: normalizeCreatedDateFilter({
      from: startDate ?? "",
      to: endDate ?? "",
    }),
  };
}

export function buildStatusDashboardReturnSearchParams(
  filters: StatusDashboardFilters
): URLSearchParams {
  persistStatusDashboardReturnFilters(filters);
  const params = new URLSearchParams();
  params.set(STATUS_DASHBOARD_FROM_QUERY, STATUS_DASHBOARD_FROM_VALUE);
  return params;
}

/** Build ticket list URL with filters from a dashboard status card. */
export function buildStatusDashboardDrillDownPath(
  status: TicketStatus,
  dashboardFilters: StatusDashboardFilters,
  panel: StatusDashboardPanel = "admin"
): string {
  const params = new URLSearchParams();
  params.set(STATUS_DASHBOARD_FROM_QUERY, STATUS_DASHBOARD_FROM_VALUE);
  params.set("status", status);

  const project = formatStatusDashboardProjectParam(
    dashboardFilters.projectFilters
  );
  if (project) params.set("project", project);

  const dates = normalizeCreatedDateFilter(dashboardFilters.createdDateFilter);
  if (dates.from) params.set("start_date", dates.from);
  if (dates.to) params.set("end_date", dates.to);

  return `${statusDashboardTicketsListPath(panel)}?${params.toString()}`;
}

export function isStatusDashboardReturnContext(
  searchParams: URLSearchParams
): boolean {
  return (
    searchParams.get(STATUS_DASHBOARD_FROM_QUERY) === STATUS_DASHBOARD_FROM_VALUE
  );
}

export function parseStatusDashboardDrillDownSearchParams(
  searchParams: URLSearchParams
): {
  projectFilters: string[];
  statusFilters: string[];
  createdDateFilter: CreatedDateFilterState;
} | null {
  if (
    searchParams.get(STATUS_DASHBOARD_FROM_QUERY) !== STATUS_DASHBOARD_FROM_VALUE
  ) {
    return null;
  }

  const status = searchParams.get("status")?.trim();
  if (!status) return null;

  const startDate = searchParams.get("start_date")?.trim();
  const endDate = searchParams.get("end_date")?.trim();

  return {
    projectFilters: parseStatusDashboardProjectParam(
      searchParams.get("project")
    ),
    statusFilters: sanitizeStatusFilterValues(
      status
        .split(",")
        .map(s => s.trim())
        .filter(Boolean)
    ),
    createdDateFilter: normalizeCreatedDateFilter({
      from: startDate ?? "",
      to: endDate ?? "",
    }),
  };
}
